import '@fontsource/fredoka/latin-500.css';
import '@fontsource/fredoka/latin-600.css';
import '@fontsource/fredoka/latin-700.css';
import '@fontsource/lilita-one/latin-400.css';
import './css/style.css';

import { fetchContent, fetchDaily, fetchPuzzle } from './api';
import { showEnd } from './end';
import { initFx } from './fx';
import { initHome, renderHome } from './home';
import { lang, t } from './i18n';
import { closePuzzle, openPuzzle } from './play';
import { initPWA } from './pwa';
import { applyChrome, onBackButton } from './platform';
import { isOpen, nextAfter } from './quest';
import { dailySolved, finish, restart, isDone, streak } from './store';
import { flushSync, startSync } from './sync';
import { hideSplash } from './splash';
import { accountOpen, close as closeAccount } from './profile';
import { openSettings } from './settings';
import { askForReview, initReview, markPartFinished } from './review';
import { applySettings } from './prefs';
import { $, applyTheme, show, showState, stateExit, toast } from './ui';
import type { Content, PuzzlePayload, PuzzleSummary } from '../shared/types';

// Le chef d'orchestre : il charge le contenu, puis passe d'un écran à l'autre. Les écrans ne se
// connaissent pas entre eux ; ils remontent ici ce que le joueur a choisi.

let content: Content;
let daily: { day: string; puzzleId: string } | null = null;
/** Le numéro du dernier écran demandé : une partie arrivée du réseau APRÈS qu'on a quitté son
 *  chargement (retour, autre partie) est jetée au lieu de s'ouvrir par-dessus. */
let ticket = 0;
/** Le chapitre de la dernière partie ouverte : le Retour d'Android depuis la fin ramène sur SON
 *  chemin, comme le bouton « Le chemin », et non au premier chapitre. */
let lastIndex: number | undefined;

async function play(index: number | null, id: string): Promise<void> {
  const mine = ++ticket;
  lastIndex = index ?? undefined;
  // Un retour possible pendant le chargement : sur un réseau lent, attendre n'est pas une obligation.
  showState('loading', undefined, () => home(lastIndex));
  let p: PuzzlePayload;
  try {
    p = await fetchPuzzle(id);
  } catch {
    if (mine === ticket)
      showState(
        'offline',
        () => void play(index, id),
        () => home(index ?? undefined),
      );
    return;
  }
  if (mine !== ticket) return;
  // « Rejouer » une partie finie la recommence de zéro ; ses étoiles restent acquises.
  if (isDone(id)) restart(id);
  const ch = index === null ? null : content.chapters[index];
  applyTheme(
    content.themes.find((th) => th.id === (ch?.themeId ?? p.themeId)) ??
      content.themes.find((th) => th.id === content.chapters[0]?.themeId),
  );
  const line = ch
    ? t('play.level', { chapter: ch.title, n: ch.puzzles.findIndex((x) => x.id === id) + 1 })
    : t('daily.bar');
  show('play');
  openPuzzle(p, line, {
    close: () => home(index ?? undefined),
    finished: (done, stars) => ended(index, done, stars),
  });
}

function ended(index: number | null, p: PuzzlePayload, aiStars: number): void {
  const wasOpen =
    index !== null && index + 1 < content.chapters.length ? isOpen(content, index + 1) : true;
  const stars = finish(p.id, p.mystery, aiStars);
  markPartFinished();
  if (index === null && daily) dailySolved(daily.day);
  const opened =
    index !== null && index + 1 < content.chapters.length && !wasOpen && isOpen(content, index + 1)
      ? content.chapters[index + 1]
      : null;
  const next = index === null ? null : nextAfter(content, index);
  // Le résultat partagé dit OÙ l'on a joué, pour que l'ami qui le reçoit puisse jouer la même :
  // le mot du jour (le même pour tous, c'est ce qui fait partager) ou la partie d'un chapitre.
  const shareLabel =
    index === null
      ? t('share.daily', {
          date: new Date(
            `${daily?.day ?? new Date().toISOString().slice(0, 10)}T12:00:00Z`,
          ).toLocaleDateString(lang, {
            day: 'numeric',
            month: 'long',
          }),
        })
      : t('share.part', {
          chapter: content.chapters[index].title,
          n: content.chapters[index].puzzles.findIndex((x) => x.id === p.id) + 1,
        });
  show('end');
  showEnd(p, {
    content,
    chapterIndex: index,
    stars,
    opened,
    streak: streak(),
    hasNext: !!next,
    shareLabel,
    back: () => home(index ?? undefined),
    next: () => next && void play(next.index, next.puzzle.id),
  });
}

function home(index?: number): void {
  ticket++;
  // On quitte une partie (ou sa fin) : ce qu'elle a laissé en file part maintenant, plutôt que
  // d'attendre deux minutes sur un écran où plus rien ne bouge (src/sync.ts, PUSH_MS).
  flushSync();
  show('home');
  renderHome(index);
}

async function openDaily(): Promise<void> {
  // Le même ticket que play() : un retour pendant le tirage ne doit pas ouvrir la partie ensuite.
  const mine = ++ticket;
  const d = await fetchDaily(lang);
  if (mine !== ticket) return;
  if (d === 'offline') return toast(t('daily.offline'));
  if (!d) return toast(t('daily.none'));
  daily = d;
  await play(null, d.puzzleId);
}

async function boot(): Promise<void> {
  showState('loading');
  try {
    content = await fetchContent(lang);
  } catch {
    showState('offline', () => void boot());
    hideSplash();
    return;
  }
  if (!content.chapters.length) {
    applyTheme(undefined);
    showState('empty', () => void boot());
    hideSplash();
    return;
  }
  if ((content as Content & { newer?: boolean }).newer) toast(t('state.newer'));
  initHome(content, {
    play: (index: number, p: PuzzleSummary) => void play(index, p.id),
    daily: () => void openDaily(),
    settings: () => {
      show('settings');
      openSettings({ back: () => home(), restored: () => undefined });
    },
  });
  home();
  // Le chemin est construit dessous : le calque peut partir à l'image suivante (il tient lui-même
  // sa durée minimale, src/splash.ts).
  requestAnimationFrame(() => hideSplash());
  // L'avis se demande ici et nulle part ailleurs : à l'arrivée sur un chemin bien peint, d'un
  // joueur qui revient (src/review.ts). Jamais au sortir d'une partie.
  void askForReview();
}

document.documentElement.lang = lang;
applySettings();
initReview();
initFx($('fx') as HTMLCanvasElement, $('bubbles'));
void applyChrome();
initPWA();
// La copie en ligne de la progression (src/sync.ts), en parallèle du contenu : le jeu ne l'attend
// jamais. Si elle ramène du neuf (une partie finie sur un autre appareil), l'accueil se redessine —
// seulement s'il est à l'écran et déjà construit, jamais par-dessus une partie en cours.
startSync(() => {
  if (content?.chapters.length && !$('home').hidden) renderHome();
});
// Le bouton Retour d'Android, du plus proche au plus lointain : le panneau du compte, puis la
// partie (comme la croix), puis la fin de partie (vers le chemin). Sur le chemin, il rend la main
// à platform.ts, qui met l'application en arrière-plan au lieu de la fermer.
void onBackButton(() => {
  if (accountOpen()) return (closeAccount(), true);
  if (!$('play').hidden) return (closePuzzle(), true);
  if (!$('end').hidden) return (home(lastIndex), true);
  if (!$('settings').hidden) return (home(), true);
  const exit = stateExit();
  if (exit) return (exit(), true);
  return false;
});
void boot();
